/**
 * Multi-select réutilisable pour choisir des utilisateurs staff comme destinataires.
 * Récupère automatiquement leur email + téléphone lors de l'envoi côté backend.
 *
 * Props :
 *   - value: string[] (staff_user_ids sélectionnés)
 *   - onChange(ids)
 *   - endpoint: URL de l'API listant les staff (défaut /api/treasury/settings/staff-recipients)
 *   - testIdPrefix: préfixe des data-testid
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StaffRecipientsSelector({
  value = [], onChange, endpoint = `${API}/treasury/settings/staff-recipients`,
  testIdPrefix = 'staff',
}) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    axios.get(endpoint)
      .then(({ data }) => setUsers(data?.items || []))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, [endpoint]);

  const toggle = (uid) => {
    const next = value.includes(uid) ? value.filter(id => id !== uid) : [...value, uid];
    onChange?.(next);
  };
  const selected = users.filter(u => value.includes(u.id));
  const filtered = users.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return `${u.firstName || ''} ${u.lastName || ''} ${u.email || ''} ${u.role || ''}`.toLowerCase().includes(q);
  });

  return (
    <div data-testid={`${testIdPrefix}-selector`}>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-medium text-slate-700">
          Ajouter des utilisateurs staff <span className="text-slate-400">(email + téléphone auto)</span>
        </label>
        <span className="text-[10px] text-slate-500">
          {value.length} sélectionné(s) / {users.length} disponibles
        </span>
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selected.map(u => (
            <span key={u.id}
                  className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 rounded-full text-[11px] px-2 py-0.5"
                  data-testid={`${testIdPrefix}-chip-${u.id}`}>
              <span className="font-medium text-blue-800">
                {u.firstName || ''} {u.lastName || ''}
              </span>
              <span className="text-blue-500">· {u.role}</span>
              <button onClick={() => toggle(u.id)} className="text-blue-400 hover:text-rose-600 ml-0.5"
                      title="Retirer" data-testid={`${testIdPrefix}-chip-remove-${u.id}`}>×</button>
            </span>
          ))}
        </div>
      )}

      <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
             placeholder="Rechercher par nom / email / rôle..."
             className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm mb-1"
             data-testid={`${testIdPrefix}-search`} />
      <div className="max-h-40 overflow-y-auto border border-slate-100 rounded-md divide-y divide-slate-50"
           data-testid={`${testIdPrefix}-list`}>
        {loading ? (
          <div className="p-3 text-center text-xs text-slate-400">Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="p-3 text-center text-xs text-slate-400">Aucun utilisateur trouvé</div>
        ) : filtered.slice(0, 50).map(u => {
          const isSelected = value.includes(u.id);
          return (
            <button key={u.id} onClick={() => toggle(u.id)}
                    className={`w-full text-left px-2 py-1.5 flex items-center justify-between text-xs hover:bg-blue-50 ${isSelected ? 'bg-blue-50' : ''}`}
                    data-testid={`${testIdPrefix}-row-${u.id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <input type="checkbox" checked={isSelected} readOnly className="h-3.5 w-3.5" />
                <div className="min-w-0">
                  <div className="text-slate-800 font-medium truncate">
                    {u.firstName || ''} {u.lastName || ''} <span className="text-slate-400 font-normal">· {u.role}</span>
                  </div>
                  <div className="text-slate-500 truncate">
                    {u.email}{u.phone ? ` · ${u.phone}` : ''}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
